import React, { useState } from 'react'
import Linkify from 'react-linkify'
import classNames from 'classnames'

import { regexEscape } from 'lib/utils/regexManager'
import i18n from 'lib/i18n'
import {
  renderWhatsAppFormatted,
  FormatFeature,
} from 'lib/utils/whatsappFormatter'
import { openUrlWrapperForFlutterApp } from 'lib/config/appBridge'

import styles from '../Text.module.scss'

const RenderChild = ({
  children,
  translateProps,
  translate,
  truncate,
  size,
  highlightText,
  linkify,
  fontSize,
  whatsappFormat,
  whatsappFormatFeatures,
}: {
  children: React.ReactNode
  translateProps: Record<string, string>
  translate: boolean
  truncate: boolean
  size: number
  highlightText: string
  linkify: boolean
  fontSize: string
  whatsappFormat: boolean
  whatsappFormatFeatures?: FormatFeature[]
}): React.ReactElement => {
  let finalText = (children as string) || (children as React.ReactElement)
  const [isReadMore, setReadMore] = useState(truncate)

  const toggleReadMore = () => setReadMore((prev) => !prev)

  const buttonClass = classNames(styles.readMoreButton, {
    [styles[fontSize]]: fontSize,
  })

  const linkifyComponentDecorator = (
    href: string,
    text: string,
    key: string
  ) => (
    <a
      href={href}
      key={key}
      target="_blank"
      rel="noreferrer"
      className={styles.link}
      onClick={openUrlWrapperForFlutterApp}
    >
      {text}
    </a>
  )

  const highlightTextFn = (text: string, highlightText: string) => {
    if (!highlightText || typeof text !== 'string') return text

    const regex = new RegExp(`(${regexEscape(highlightText)})`, 'gi')
    const normalizedHighlight = highlightText.toLowerCase()

    const parts = text
      .split(regex)
      .map((part, index) => (
        <React.Fragment key={index}>
          {part.toLowerCase() === normalizedHighlight ? (
            <span className={styles.highlight}>{part}</span>
          ) : (
            part
          )}
        </React.Fragment>
      ))

    return <>{parts}</>
  }

  const readMoreText = (text: string, size: number) => {
    return `${text.slice(0, size)}...`
  }

  if (translate) {
    try {
      finalText = i18n?.t?.(finalText as string, translateProps)
    } catch (error) {
      return finalText as React.ReactElement
    }
  }

  // Truncation must keep finalText a string here so the WhatsApp format and
  // highlight passes below still run on it — the Read More/Less button is
  // attached after them (a long *bold* message stays bold either way).
  let readMoreButton: React.ReactElement | null = null
  if (truncate && typeof finalText === 'string' && finalText?.length > size) {
    readMoreButton = (
      <button className={buttonClass} onClick={toggleReadMore}>
        {isReadMore ? 'Read More' : 'Read Less'}
      </button>
    )
    if (isReadMore) {
      finalText = readMoreText(finalText, size)
    }
  }

  // WhatsApp format must run BEFORE linkify to parse the string first
  if (whatsappFormat && typeof finalText === 'string') {
    finalText = renderWhatsAppFormatted(
      finalText,
      whatsappFormatFeatures
    ) as React.ReactElement
  }

  if (highlightText && typeof finalText === 'string') {
    finalText = highlightTextFn(finalText as string, highlightText)
  }

  if (readMoreButton) {
    finalText = (
      <>
        {finalText}
        {readMoreButton}
      </>
    )
  }

  if (linkify) {
    const linkifyChildren = React.Children.toArray(
      finalText === undefined || finalText === null ? '' : finalText
    )
    finalText = (
      <Linkify
        componentDecorator={(href: string, text: string, key: number) =>
          linkifyComponentDecorator(href, text, key.toString())
        }
      >
        {linkifyChildren}
      </Linkify>
    )
  }

  return (finalText || '') as React.ReactElement
}

export default RenderChild
